import { useContext } from 'react';

import { ToastContext } from './ToastProvider';
import { type ToastState } from './types';

/**
 * Hook to open and close a toast message. Needs to be used in conjunction with ToastProvider.
 * @returns An object with the following properties:
 *  - close: Function to close the toast message. This does not need to be explicitly called each time as the toast message automatically disappears after 5 seconds.
 *  - open: Function to open the toast message.
 * @example
 * const toast = useToast();
 * toast.open({ title: 'Oops, something has gone wrong', description: 'Please try again', status: 'error' });
 */

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast hook must be used within a ToastProvider');
  }

  const { setToastState } = context;

  function close() {
    setToastState({});
  }

  /**
   * Open a toast message.
   * @param classname - The class name of the toast message (optional).
   * @param description - The description of the toast message (optional).
   * @param status - The status of the toast message (required).
   * @param title - The title of the toast message (required).
   */
  function open({ classname, description, status, title }: ToastState) {
    setToastState({
      classname,
      description,
      status,
      title,
    });
  }

  return { close, open };
}
